import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import { AuthToken } from '../types';
import { APPLICATION_NAME } from '../constants/AppConfig';
import { parseApiError } from '../utils/errorUtils';
import AppLogo from '../images/logo.png';
import './LoginPage.css';

interface FieldErrors {
  email?: string;
  password?: string;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [needsActivation, setNeedsActivation] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { setCompany } = useModule();

  // Set by RegisterPage after a successful signup — the company stays inactive
  // until the emailed activation link is clicked.
  const justRegistered = Boolean((location.state as { registered?: boolean } | null)?.registered);

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!email.trim()) {
      errors.email = t('login.emailRequired');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = t('login.emailInvalid');
      isValid = false;
    }

    if (!password) {
      errors.password = t('login.passwordRequired');
      isValid = false;
    }

    setFieldErrors(errors);

    // Show the first specific error in the banner instead of a generic message.
    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear both the inline field error and the banner so the user gets
    // immediate feedback that the issue is being addressed.
    setFieldErrors((prev) => ({ ...prev, email: undefined }));
    setError(null);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setFieldErrors((prev) => ({ ...prev, password: undefined }));
    setError(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await apiService.post<AuthToken>('/auth/login', { email, password });

      if (response.data.success && response.data.data) {
        const { token, user, company } = response.data.data;
        login(token, user);
        setCompany(company ?? null);
        const isCashierOnly = user.roles?.some((r) => r.name === 'Cashier') &&
          !user.roles?.some((r) => r.name === 'Admin' || r.name === 'Manager');
        navigate(isCashierOnly ? '/cashier/order' : '/apps');
      }
    } catch (err: unknown) {
      const parsed = parseApiError(err, t('login.invalidCredentials'));
      setError(parsed.message);
      setNeedsActivation(parsed.code === 'COMPANY_NOT_VERIFIED');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={AppLogo} alt={APPLICATION_NAME} className="login-logo" />
            <h1 className="login-title">{t('login.welcomeBack')}</h1>
            <p className="login-subtitle">{APPLICATION_NAME}</p>
          </div>

          {/* noValidate disables browser built-in validation so our custom
              messages are always shown instead of the browser defaults. */}
          <form onSubmit={handleLogin} className="login-form" noValidate>

            {justRegistered && !error && (
              <div className="login-registered-notice" role="status">
                {t('register.success')}
              </div>
            )}

            {error && (
              <div className="login-error" role="alert">
                <span>
                  {error}
                  {needsActivation && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="login-resend-link"
                        onClick={() => navigate('/activate')}
                      >
                        {t('login.resendActivation')}
                      </button>
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className="login-error-close"
                  onClick={() => setError(null)}
                  aria-label={t('common.close')}
                >
                  &times;
                </button>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="login-email">
                {t('login.email')}
              </label>
              <input
                id="login-email"
                type="email"
                className={`form-control${fieldErrors.email ? ' is-invalid' : ''}`}
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                autoComplete="email"
                autoFocus
                maxLength={150}
              />
              {fieldErrors.email && (
                <small className="form-error">{fieldErrors.email}</small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">
                {t('login.password')}
              </label>
              <div className="password-input-wrapper">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control${fieldErrors.password ? ' is-invalid' : ''}`}
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={loading}
                  autoComplete="current-password"
                  maxLength={128}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldErrors.password && (
                <small className="form-error">{fieldErrors.password}</small>
              )}
            </div>

            <button
              type="submit"
              className="btn-save"
              disabled={loading}
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>

            <button
              type="button"
              className="forgot-password-below"
              onClick={() => navigate('/forgot-password')}
            >
              {t('login.forgotPassword')}
            </button>
          </form>

          <div className="login-footer">
            <div className="login-divider">
              <span>{t('login.noAccount')}</span>
            </div>
            <button
              type="button"
              className="btn-register"
              onClick={() => navigate('/register')}
            >
              {t('register.createCompany')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
